import { formatDate, translate } from '@/lib/i18n/runtime';
import type { Locale } from '@/lib/i18n/locales';
import type { TimeFormat } from '@/store/theme-store';

/** Civil-day difference in the existing browser/business wall-clock zone.
 * Using elapsed 24h blocks would change weekday labels across daylight saving. */
export function localDayDistance(from: Date, to: Date): number {
  const day = (value: Date) => Date.UTC(value.getFullYear(), value.getMonth(), value.getDate());
  return Math.round((day(to) - day(from)) / 86_400_000);
}

export function formatListClock(value: Date, preference: TimeFormat, locale: Locale): string {
  return formatDate(value, {
    hour: preference === '24h' ? '2-digit' : 'numeric', minute: '2-digit',
    hourCycle: preference === '24h' ? 'h23' : 'h12',
  }, locale);
}

export function formatListTime(iso: string | undefined, preference: TimeFormat, locale: Locale, now = Date.now()): string {
  if (!iso) return '';
  const date = new Date(iso), today = new Date(now);
  if (!Number.isFinite(date.getTime())) return '';
  const days = localDayDistance(date, today);
  if (days === 0) return formatListClock(date, preference, locale);
  if (days === 1) return translate('date.yesterday', {}, 'inbox', locale);
  if (days > 1 && days < 7) return formatDate(date, { weekday: 'short' }, locale);
  return formatDate(date, { day: 'numeric', month: 'short', ...(date.getFullYear() !== today.getFullYear() ? { year: '2-digit' as const } : {}) }, locale);
}

/** Presentation only: the caller must use the server's is_snoozed flag. */
export function formatListSnooze(iso: string | null | undefined, now: number, preference: TimeFormat, locale: Locale): string {
  const until = iso ? new Date(iso) : null;
  if (!until || !Number.isFinite(until.getTime())) return translate('snooze.generic', {}, 'inbox', locale);
  const today = new Date(now), time = formatListClock(until, preference, locale);
  const days = localDayDistance(today, until);
  if (days === 0 || days === 1) return translate(days === 0 ? 'snooze.today' : 'snooze.tomorrow', { time }, 'inbox', locale);
  const date = formatDate(until, { day: 'numeric', month: 'short', ...(until.getFullYear() !== today.getFullYear() ? { year: 'numeric' as const } : {}) }, locale);
  return translate('snooze.date', { date, time }, 'inbox', locale);
}
